import SuggestMemoryPartRecord from "models/view-models/conversations/suggest-memory-part-record";
import React, { useCallback, useState } from "react";
import MemorySuggestion from "../memory-suggestion";
import { useConversationActions } from "utilities/atoms/conversations/current-conversation/use-conversation-actions.hook";
import { MemoryStatus } from "models/enumerations/conversations/memory-status";
import { BasePartComponentProps } from "./message-parts";
import ChatMemoriesModal from "organisms/modals/chat-memories-modal/chat-memories-modal";
import useModalActions from "utilities/hooks/use-modal-actions";
import useMemories, {
    MAX_MEMORIES_PER_USER,
} from "utilities/hooks/domain/chat/use-memories";
import { t } from "utilities/localization-utils";
import { ToastManager } from "utilities/toast/toast-manager";

interface SuggestedMemoryPartProps extends BasePartComponentProps {
    part: SuggestMemoryPartRecord;
}

const SuggestedMemoryPart: React.FC<SuggestedMemoryPartProps> = ({
    part,
    messageId,
}) => {
    const { updateMemoryStatus } = useConversationActions();
    const [isSaving, setIsSaving] = useState(false);
    const [isDismissing, setIsDismissing] = useState(false);
    const { memories, refetch: refetchMemories } = useMemories();
    const {
        handleOpen: handleMemoriesModalOpen,
        isOpen: isMemoriesModalOpen,
        handleClose: handleMemoriesModalClose,
    } = useModalActions();

    const handleSave = useCallback(async () => {
        if (memories?.rowCount && memories.rowCount >= MAX_MEMORIES_PER_USER) {
            ToastManager.error(
                t("memory-limit-reached", { max: MAX_MEMORIES_PER_USER }),
                {
                    autoClose: 5000,
                }
            );
            return;
        }

        if (messageId && part.memory?.id) {
            setIsSaving(true);

            try {
                await updateMemoryStatus({
                    messageId: messageId,
                    memoryId: part.memory.id,
                    status: MemoryStatus.Accepted,
                });
                await refetchMemories();
            } finally {
                setIsSaving(false);
            }
        }
    }, [
        memories?.rowCount,
        messageId,
        part.memory?.id,
        updateMemoryStatus,
        refetchMemories,
    ]);

    const handleDismiss = useCallback(async () => {
        if (messageId && part.memory?.id) {
            setIsDismissing(true);

            try {
                await updateMemoryStatus({
                    messageId: messageId,
                    memoryId: part.memory.id,
                    status: MemoryStatus.Rejected,
                });
            } finally {
                setIsDismissing(false);
            }
        }
    }, [part.memory?.id, messageId, updateMemoryStatus]);

    const handleUndo = useCallback(() => {
        if (messageId && part.memory?.id) {
            updateMemoryStatus({
                messageId: messageId,
                memoryId: part.memory.id,
                status: MemoryStatus.Suggested,
            });
        }
    }, [part.memory?.id, messageId, updateMemoryStatus]);

    return (
        <>
            <MemorySuggestion
                content={part.memory?.content}
                count={memories?.rowCount ?? 0}
                status={part.memory?.status}
                isSaving={isSaving}
                isDismissing={isDismissing}
                onSave={handleSave}
                onDismiss={handleDismiss}
                onManage={handleMemoriesModalOpen}
                onUndo={handleUndo}
            />
            <ChatMemoriesModal
                memories={memories?.resultObjects}
                refetchMemories={refetchMemories}
                isOpen={isMemoriesModalOpen}
                onClose={handleMemoriesModalClose}
            />
        </>
    );
};

export default SuggestedMemoryPart;
